// frontend/src/core/errors/ErrorBoundary.tsx
import * as React from 'react';
import { ErrorState } from '../../design-system/components/ErrorState';
import { MeshBackground } from '../../design-system/components/MeshBackground';
import { NoiseOverlay } from '../../design-system/components/NoiseOverlay';
import { captureException } from '../observability/sentry';

interface Props { children: React.ReactNode; }
interface State { error: Error | null; }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State { return { error }; }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
    captureException(error, { componentStack: info.componentStack });
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-background text-foreground">
        <MeshBackground />
        <NoiseOverlay />
        <ErrorState
          title="The app hit a snag"
          description={`${this.state.error.message ?? 'Unknown error'}\n\nReload the page or click Try again. If it keeps happening, contact support.`}
          onRetry={() => { this.reset(); window.location.reload(); }}
        />
      </div>
    );
  }
}
